import { useEffect, useRef, useState } from "react";

/**
 * Sector image with an optional looping clip next to it (same path, .mp4).
 * Optimized for performance:
 * - IntersectionObserver pauses offscreen videos to eliminate GPU/CPU decode lag.
 * - preload="none" prevents parallel downloads from saturating bandwidth.
 * - Hardware composited with translateZ(0) to avoid layout thrashing.
 * - Falls back to static image on prefers-reduced-motion or when inactive.
 */
export function LoopMedia({
  image,
  alt,
  className,
  active = true,
}: {
  image: string;
  alt: string;
  className?: string;
  active?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [motion, setMotion] = useState(false);

  useEffect(() => {
    setMotion(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !motion || !active) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        }
      },
      { rootMargin: "120px 0px", threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [motion, active]);

  if (!motion || !active) {
    return (
      <img
        className={className}
        src={image}
        alt={alt}
        width={640}
        height={400}
        loading="lazy"
        decoding="async"
      />
    );
  }

  const videoSrc = image.replace(/\.(jpg|jpeg|webp|png)$/, ".mp4");

  return (
    <video
      ref={videoRef}
      className={className}
      src={videoSrc}
      poster={image}
      aria-label={alt}
      muted
      loop
      playsInline
      preload="none"
      style={{
        transform: "translateZ(0)",
        willChange: "transform",
      }}
    />
  );
}
